import moment from "moment-timezone";
import { useAnalyticsContext } from '../../config/GoogleTagManagerEvents';
import { useCartState } from '../../contexts/cart';
import { useFilterState } from '../../components/cap';
import useAnalytics from './useAnalytics';
import { useCheckDeliveryCharge } from '../useCheckDeliveryCharge';
import { useCheckUser } from '../useCheckUser';
import { useSunbeltLocation } from '../useSunbeltLocation';
import useReservation from '../../components/checkoutv2/hooks/useReservation';
import { checkIsEditQuoteFlow, getRentalDuration, isCheckoutPage } from '../../components/global/utils/commonUtils';
import { cardTypeMapper } from '../../components/checkoutv2/checkoutPayment/paymentHelper';
import { logError } from '../../components/global/utils/logger';
import { getProducts } from '../../constants/analyticsConstants/getProducts';
import { STORAGE_CONFIG } from '../../constants/storageConfig';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../constants/analyticsConstants/Ecommerce';
import { VARIABLE_CONFIG } from '../../constants/analyticsConstants/Variables';
import { EVENT_PAGE_NAMES_CONFIG } from '../../constants/analyticsConstants/Page';
import { DELIVERY_CHARGE_FLAG } from '../../constants/cartConstants';
import { USER_TYPE } from '../../constants/userDetailsConstants';
import { GENERATE_QUOTE, UPDATE_QUOTE } from "../../components/checkoutv2/constants";

const useCheckoutAnalytics = () => {
    const [
        {
            cart,
            howToGetYourOrderDetails,
            projectInfo,
            consumables,
            timeZoneID,
            orderSummaryDetails,
            paymentData,
            paymentTokenData,
            userAccount,
            isInEditQuoteMode,
            quoteData
        }
    ] = useCartState();
    const [{ projectDetails, startDate, endDate, viewCart }] = useFilterState();
    const {
        sendEventsForEcommerceAction,
        sendEventsForEcommercePage,
        sendEventsForPageUser,
        sendEventsForUpdateVirtualPath,
        sendEventsForEcommerceCheckoutOption,
        sendEventsForEcommerceCheckout,
        sendEventsForEcommerceQuoteSubmission,
        sendEventsForEcommercePurchase
    } = useAnalyticsContext();
    const [{ payloadEcommerceActionAnalytics, payloadEcommerceLocationActionAnalytics }] = useAnalytics();
    const sunbeltLocation = useSunbeltLocation();
    const deliveryChargeFlag = useCheckDeliveryCharge();
    const [{ getConfirmationMessage }] = useReservation();
    const userType = useCheckUser();
    const orderEstimates = cart?.estimatesResponse?.estimate;
    const { rentalAmount, deliveryPickUpCharges, fuelCharges, rppCharges, salesTax, allOtherCharges } =
        orderEstimates?.totals || {};
    const envFeeObj = orderEstimates?.miscCharges?.find(item => item.type.indexOf('ENVIRONMENTAL') > -1);
    const { charge = 0 } = envFeeObj || {};
    const selectedStoreDetails = JSON.parse(
        localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.SELECTEDSTOREDETAILS) || '{}'
    );

    const checkoutStepPayload = (formStep, currentStep, stepTypeValue, reservation = false) => {
        const displayQuoteId = localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.DISPLAY_QUOTE_ID) || '';
        const isEditQuoteFlow = checkIsEditQuoteFlow();
        let currentStepName;
        // 'reservation' will be true only when we trigger event for submit-reservation
        if (reservation) {
            currentStepName = Object.keys(formStep)[0];
        } else {
            // 'stepTypeValue' will be 0 if we trigger step_viewed event
            // 'stepTypeValue' will be 1 if we trigger step_completed event
            currentStepName = Object.keys(formStep)[currentStep - (stepTypeValue + 1)];
        }
        if (isInEditQuoteMode && currentStepName === GENERATE_QUOTE) {
            currentStepName = UPDATE_QUOTE;
        }
        const checkoutSteps = {
            page_title: getStepTitle(VARIABLE_CONFIG.CHECKOUT_QUOTE_PAGE_TITLE[currentStepName]),
            page_location: getStepLocation(VARIABLE_CONFIG.CHECKOUT_QUOTE_PAGE_LOCATION[currentStepName]),
            step_number: currentStep - stepTypeValue
        };
        if (isEditQuoteFlow || isInEditQuoteMode) {
            checkoutSteps.quote_id =  isEditQuoteFlow ? displayQuoteId : quoteData?.orderId;
        }
        return checkoutSteps;
    };

    const triggerCheckoutEvents = eventName => {
        try {
            sendEventsForEcommercePage(
                eventName,
                localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.COMPANYID) == 2
                    ? VARIABLE_CONFIG.CURRENCY_CODE.CANADA
                    : VARIABLE_CONFIG.CURRENCY_CODE.USA
            );
            sendEventsForPageUser(EVENT_PAGE_NAMES_CONFIG.PAGE_USER, payloadEcommerceLocationActionAnalytics());
        } catch (error) {
            logError(error, false, 'triggerCheckoutEvents');
        }
    };

    const triggerCheckoutStepEvents = (isStepForward, formStep, currentStep) => {
        try {
            if (isStepForward) {
                sendEventsForEcommerceAction(
                    EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CHECKOUT_STEP_COMPLETED,
                    payloadEcommerceActionAnalytics(),
                    checkoutStepPayload(formStep, currentStep, 1)
                );
            }
            sendEventsForEcommerceAction(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CHECKOUT_STEP_VIEWED,
                payloadEcommerceActionAnalytics(),
                checkoutStepPayload(formStep, currentStep, 0)
            );
        } catch (error) {
            logError(error, false, 'triggerCheckoutStepEvents');
        }
    };

    const getDate = selectedDate => {
        const stringToDate = new Date(selectedDate);
        return moment(stringToDate).format('l');
    };

    const sendEventsForPickup = (stepNumber, startDate, endDate) => {
        try {
            const currencyCode =
                localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.COMPANYID) == 2
                    ? VARIABLE_CONFIG.REGION.CANADA
                    : VARIABLE_CONFIG.REGION.US;
            sendEventsForEcommerceCheckoutOption(stepNumber, {
                pickupDate: getDate(startDate),
                pickupTime: howToGetYourOrderDetails?.selectedPickupTime,
                returnDate: getDate(endDate),
                returnTime: howToGetYourOrderDetails?.selectedReturnTime,
                rentalDuration: getRentalDuration(startDate, endDate),
                timeZone: timeZoneID,
                rentalSubtotal: rentalAmount,
                estimatedSubtotal: rentalAmount + salesTax,
                pickupInstructions:
                    projectInfo?.accessNotes ||
                    projectDetails?.accessNotes ||
                    VARIABLE_CONFIG.EMPTY_STRING.PAGE_UNSPECIFIED,
                sunbeltLocation: sunbeltLocation
            });
            sendEventsForUpdateVirtualPath(
                VARIABLE_CONFIG.VIRTUAL_PAGE_TITLE.CHECKOUT_PICKUP_LOCATION,
                `/${currencyCode}/${VARIABLE_CONFIG.VIRTUAL_PAGE_URL.PICKUP_LOCATION}`
            );
        } catch (error) {
            logError(error, false, 'sendEventsForPickup');
        }
    };

    const getDeliveryFee = payload => {
        if (deliveryChargeFlag === DELIVERY_CHARGE_FLAG.TBD) {
            return DELIVERY_CHARGE_FLAG.TBD;
        }
        return payload?.deliveryCharge;
    };

    const getSubmissionActionPayload = payload => {
        const { pc, fulfillmentPercent } = selectedStoreDetails || {};
        return {
            revenue: parseFloat(orderSummaryDetails?.estimatedSubtotal),
            tax: salesTax,
            subtotal: rentalAmount + charge,
            otherFees: parseFloat(allOtherCharges).toFixed(2),
            rentalProtectionPlan: payload?.optOutRPP === 'Y' ? rppCharges : 0,
            fuelConveniencePlan: payload?.optOutFuelCharge === 'Y' ? fuelCharges : 0,
            environmentalFee: charge,
            deliveryFee: getDeliveryFee(payload),
            transportationSurcharge: deliveryPickUpCharges,
            zipBill: payload?.zip,
            zipShip: payload?.zip,
            fulfillment_pc: pc || viewCart?.pc,
            fulfillment_ratio: fulfillmentPercent
        };
    };

    const triggerReservationSuccessEvents = (datesObj, payload, id, currentStep) => {
        try {
            const actionPayload = getSubmissionActionPayload(payload) || {};
            sendEventsForEcommerceCheckout(currentStep + 1, getProducts(cart, datesObj, false, consumables));
            sendEventsForEcommercePurchase({
                ...actionPayload,
                id,
                currencyCode: payload?.companyId === '2' ? VARIABLE_CONFIG.CURRENCY_CODE.CANADA : VARIABLE_CONFIG.CURRENCY_CODE.USA,
                payMethod: userType !== USER_TYPE.CREDIT
                    ? cardTypeMapper(paymentData?.selectedPaymentDetails?.paymentType)?.toLowerCase() ||
                            paymentTokenData?.ccBrand
                    : VARIABLE_CONFIG.ACCOUNT_PAYMENT_TYPE.CREDIT,
                rentalMethod: payload?.deliveryCharge === 0
                    ? VARIABLE_CONFIG.RENTAL_METHOD.PICKUP
                    : VARIABLE_CONFIG.RENTAL_METHOD.DELIVERY,
                reservationResult: getConfirmationMessage().message,
                rentalDuration: getRentalDuration(startDate, endDate),
                sunbeltLocation,
                products: getProducts(cart, datesObj, false, consumables)
            });
            triggerCommonSuccessEvents({ SUBMIT_RESERVATION: 'SUBMIT_RESERVATION' }, currentStep);
        } catch (error) {
            logError(error, false, 'triggerReservationSuccessEvents');
        }
    };

    const triggerQuoteSuccessEvents = (datesObj, payload, id, currentStep, isUpdateQuote) => {
        try {
            const actionPayload = getSubmissionActionPayload(payload) || {};
            sendEventsForEcommerceQuoteSubmission(
                {
                    ...actionPayload,
                    id,
                    step: currentStep + 1
                },
                getProducts(cart, datesObj, false, consumables),
                isUpdateQuote
            );
            triggerCommonSuccessEvents(
                { GENERATE_QUOTE: isUpdateQuote ? UPDATE_QUOTE : GENERATE_QUOTE },
                currentStep
            );
        } catch (error) {
            logError(error, false, 'triggerQuoteSuccessEvents');
        }
    };

    const triggerCommonSuccessEvents = (formStep, currentStep) => {
        try {
            sendEventsForUpdateVirtualPath(
                VARIABLE_CONFIG.VIRTUAL_PAGE_TITLE.CONFIRMATION,
                `/${
                    localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.COMPANYID) == 2
                        ? VARIABLE_CONFIG.REGION.CANADA
                        : VARIABLE_CONFIG.REGION.US
                }/${VARIABLE_CONFIG.VIRTUAL_PAGE_TITLE.CONFIRMATION}`
            );
            sendEventsForEcommerceAction(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CHECKOUT_STEP_COMPLETED,
                payloadEcommerceActionAnalytics(),
                checkoutStepPayload(formStep, currentStep, 0, true)
            );
        } catch (error) {
            logError(error, false, 'triggerCommonSuccessEvents');
        }
    };

    const triggerQuotePDFDownloadEvents = (quote_id) => {
        try {
            const user = {
                account: userAccount?.accountNumber || VARIABLE_CONFIG.ECOMMERCE.UNDEFINED_WITHOUT_STRINGIFY,
                account_name:
                    userAccount?.accountName || VARIABLE_CONFIG.ECOMMERCE.UNDEFINED_WITHOUT_STRINGIFY,
                project: projectDetails?.projectName || VARIABLE_CONFIG.ECOMMERCE.UNDEFINED_WITHOUT_STRINGIFY
            };
            const ecommerce = {
                quote_id,
                type: 'quote',
                rent_value: parseFloat(orderSummaryDetails?.estimatedSubtotal),
                delivery_method: viewCart?.isInStorePickup ? 'pickup' : 'delivery',
                start_date: startDate,
                end_date: endDate
            };
            sendEventsForEcommerceAction(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_QUOTES_DOWNLOAD,
                { user, ecommerce }
            );
        } catch (error) {
            logError(error, false, 'triggerQuotePDFDownloadEvents');
        }
    };

    const getStepTitle = stepName => {
        try {
            let pageName;
            if (isCheckoutPage()) {
                pageName = isInEditQuoteMode ? VARIABLE_CONFIG.VIRTUAL_PAGE_TITLE.CONVERT_QUOTE : VARIABLE_CONFIG.VIRTUAL_PAGE_TITLE.CHECKOUT;
            } else {
                pageName = isInEditQuoteMode ? VARIABLE_CONFIG.VIRTUAL_PAGE_TITLE.UPDATE_QUOTE : VARIABLE_CONFIG.VIRTUAL_PAGE_TITLE.QUOTE;
            }
            return `${pageName} - ${stepName}`;
        } catch (error) {
            logError(error, false, 'getStepTitle', [stepName]);
        }
    };

    const getStepLocation = location => {
        try {
            let pageLocation;
            if (isCheckoutPage()) {
                pageLocation = isInEditQuoteMode ? VARIABLE_CONFIG.VIRTUAL_PAGE_URL.CONVERT_QUOTE : VARIABLE_CONFIG.VIRTUAL_PAGE_URL.CHECKOUT;
            } else {
                pageLocation = isInEditQuoteMode ? VARIABLE_CONFIG.VIRTUAL_PAGE_URL.UPDATE_QUOTE : VARIABLE_CONFIG.VIRTUAL_PAGE_URL.QUOTE;
            }
            return pageLocation + location;
        } catch (error) {
            logError(error, false, 'getStepLocation', [location]);
        }
    };

    return [
        {
            triggerCheckoutEvents,
            triggerCheckoutStepEvents,
            sendEventsForPickup,
            triggerReservationSuccessEvents,
            triggerQuoteSuccessEvents,
            triggerQuotePDFDownloadEvents,
            getStepTitle,
            getStepLocation
        }
    ];
};

export default useCheckoutAnalytics;
